import { useMemo } from "react";
import { useAppSelector } from "./useAppSelector";
import { canAccessMenu, normalizeGrantedMenu, normalizeMenuPath } from './menuPermission';
import { isMenuPreviewEnabled } from '../config/menuPreview';
import { normalizeActionCode } from './actionPermission';

type PermissionCheckOptions = {
    module?: string;
    action?: string;
    code?: string;
};

const normalize = (value?: string | null) => (value || "").trim().toLowerCase();
const centralModule = (value?: string) => ({dot_hoc:'bskt_dothoc',mon_hoc:'bskt_monhoc',lop_hoc:'bskt_lop',diem_nhap:'bskt_diem'} as Record<string,string>)[normalize(value)] || normalize(value);
const centralAction = (value?: string) => normalize(value)==='edit'?'update':normalize(value);
const normalizePath = normalizeMenuPath;

export const usePermission = () => {
    const isMenuPreview = isMenuPreviewEnabled();
    const user = useAppSelector((state) => state.auth.user);
    const userMenus = user?.menus;
    const userActions = user?.actions;
    const hasApplicationAccess = isMenuPreview || (user?.has_application_access ?? false);

    const menus = useMemo(() => {
        const source = Array.isArray(userMenus) ? userMenus : [];
        return source.map(normalizeGrantedMenu);
    }, [userMenus]);

    const actions = useMemo(() => {
        const source = Array.isArray(userActions) ? userActions : [];
        return source.map((item: any) => ({
            code: normalizeActionCode(item?.Code ?? item?.code),
            module: centralModule(item?.ModuleName ?? item?.moduleName),
            action: centralAction(item?.ActionName ?? item?.actionName)
        }));
    }, [userActions]);

    const hasConfiguredMenus = menus.length > 0;
    const hasConfiguredActions = actions.length > 0;
    const menusDefined = Array.isArray(userMenus);

    const hasMenuAccess = (permission?: string, path?: string | null) => {
        if (isMenuPreview) return true;
        if (!menusDefined) return false;
        if (!hasConfiguredMenus) return false;

        const permissionCode = normalize(permission);
        const normalizedPath = normalizePath(path);

        return canAccessMenu(menus, permissionCode, normalizedPath);
    };

    const hasAction = ({ module, action, code }: PermissionCheckOptions) => {
        if (isMenuPreview) return true;
        if (!hasConfiguredActions || !hasApplicationAccess) return false;

        const normalizedCode = normalizeActionCode(code);
        const normalizedModule = centralModule(module);
        const normalizedAction = centralAction(action);

        return actions.some((item) => {
            if (normalizedCode && item.code === normalizedCode) return true;
            if (normalizedModule && normalizedAction && item.module === normalizedModule && item.action === normalizedAction) {
                return true;
            }
            return false;
        });
    };

    return {
        isMenuPreview,
        hasApplicationAccess,
        hasMenuAccess,
        hasAction,
        menus,
        actions
    };
};
